import type { Container, Sprite } from 'pixi.js';
import { makeSprite } from '@/assets/resolver';
import type { BossDef } from './boss-base';

/**
 * B4 Twin Serpents — def, tuning and chain types
 * (class logic in serpent.ts). docs/02 + docs/12 J3.
 */
export const SERPENT: BossDef = {
  id: 'serpent',
  nameKey: 'boss.serpent',
  tier: 3,
  minRoom: 1,
  baseHp: 1100,
  radius: 26, // head radius — custom collision overrides use it per head
  bodyKey: 'boss/serpent/head',
  movement: 'orbit', // unused — fully custom movement
  contactDamage: 1,
  phases: [{ hpPct: 1.0, patterns: [] }],
  evolve: [
    {
      // Void pulse: the lead head throbs out a ring (fired from boss x/y)
      tier: 3,
      pattern: {
        type: 'radial', count: 10, interval: 4.4, spinDeg: 18,
        bullet: 'bullet/enemy/ring', speed: 150, radius: 10, damage: 2,
      },
    },
  ],
};

export const SEG_COUNT = 11;
export const SEG_DIST = 27;
export const SEG_RADIUS = 15;
export const HEAD_RADIUS = 26;
export const PATROL_RADIUS = 330;
export const PATROL_SPEED = 0.55; // rad/s
/** Slither (docs/02 B2): the patrol weaves in and out — S-curves, not a loop */
export const SLITHER_AMP = 95;
export const SLITHER_FREQ = 2.4; // rad/s
export const STEER = 3.2;
export const SPEED = 240;
export const DIVE_SPEED = 460;
export const DIVE_INTERVAL = 4.6;
export const WINDUP = 0.55;
export const DIVE_TIME = 0.85;
export const FIRE_INTERVAL = 2.2;
export const DRIP_INTERVAL = 0.5;
export const ENRAGE_MUL = 1.4;
// J3 fire variations (docs/12)
export const TRAIL_INTERVAL = 0.09;
export const CROSS_INTERVAL = 7.0;
/** Tier extras: dive pull-out burst, then the wide 5-shot head fan */
export const BURST_MIN_TIER = 1;
export const WIDE_FAN_TIER = 2;
export const BURST_COUNT = 8;
export const FAN_NARROW: ReadonlyArray<number> = [-0.3, 0, 0.3];
export const FAN_WIDE: ReadonlyArray<number> = [-0.52, -0.26, 0, 0.26, 0.52];
export const CROSS_OFFSETS: ReadonlyArray<number> = [-0.22, 0.22];
export const SHOT_SPEED = { head: 250, cross: 290, burst: 165 } as const;

export interface Node {
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  sprite: Sprite;
}

export interface Snake {
  alive: boolean;
  hp: number;
  head: Node;
  vx: number;
  vy: number;
  segs: Node[];
  phase: number;
  dir: 1 | -1;
  diveTimer: number;
  windup: number;
  diving: number;
  diveX: number;
  diveY: number;
  fireTimer: number;
  dripTimer: number;
  dripIndex: number;
  /** Dive wake mine timer (J3) */
  trailTimer: number;
}

/** Build one snake's sprite chain inside the boss view. */
export function makeSnake(
view: Container, hp: number, phase: number, dir: 1 | -1, hx: number, hy: number,
): Snake {
  const startX = hx + Math.cos(phase) * PATROL_RADIUS;
  const startY = hy + Math.sin(phase) * PATROL_RADIUS;

  const makeNode = (key: string, x: number, y: number): Node => {
    const sprite = makeSprite(key);
    view.addChild(sprite);
    return { x, y, prevX: x, prevY: y, sprite };
  };

  const head = makeNode('boss/serpent/head', startX, startY);
  const segs: Node[] = [];
  for (let i = 0; i < SEG_COUNT; i++) {
    const node = makeNode('boss/serpent/segment', startX - (i + 1) * SEG_DIST, startY);
    node.sprite.scale.set(1 - (i / SEG_COUNT) * 0.45); // tapers toward tail
    segs.push(node);
  }

  return {
    alive: true, hp, head, vx: 0, vy: 0, segs, phase, dir,
    diveTimer: DIVE_INTERVAL * (0.6 + phase / 6), windup: 0, diving: 0,
    diveX: 0, diveY: 0,
    fireTimer: 1.5 + phase * 0.3, dripTimer: DRIP_INTERVAL, dripIndex: 0,
    trailTimer: TRAIL_INTERVAL,
  };
}


/** Steer the head toward a target, then drag the follow-the-leader chain. */
export function steerSnake(
  s: Snake, targetX: number, targetY: number, maxSpeed: number, dt: number,
): void {
  const head = s.head;
  const dx = targetX - head.x;
  const dy = targetY - head.y;
  const dist = Math.hypot(dx, dy) || 1;
  s.vx += (dx / dist) * maxSpeed * STEER * dt;
  s.vy += (dy / dist) * maxSpeed * STEER * dt;
  const v = Math.hypot(s.vx, s.vy);
  if (v > maxSpeed) {
    s.vx = (s.vx / v) * maxSpeed;
    s.vy = (s.vy / v) * maxSpeed;
  }
  head.x += s.vx * dt;
  head.y += s.vy * dt;

  let leadX = head.x;
  let leadY = head.y;
  for (const seg of s.segs) {
    seg.prevX = seg.x;
    seg.prevY = seg.y;
    const sx = leadX - seg.x;
    const sy = leadY - seg.y;
    const d = Math.hypot(sx, sy);
    if (d > SEG_DIST) {
      const pull = (d - SEG_DIST) / d;
      seg.x += sx * pull;
      seg.y += sy * pull;
    }
    leadX = seg.x;
    leadY = seg.y;
  }
}

/** Head or any (invulnerable) segment touching the player. */
export function snakeTouches(s: Snake, px: number, py: number, playerRadius: number): boolean {
  if (Math.hypot(s.head.x - px, s.head.y - py) < HEAD_RADIUS + playerRadius) return true;
  for (const seg of s.segs) {
    if (Math.hypot(seg.x - px, seg.y - py) < SEG_RADIUS + playerRadius) return true;
  }
  return false;
}
